import actionTypes from '../actions/actionTypes'
import makeUniqueId from 'helpers/makeUniqueId'
import { topInformationGenerator } from 'redux/reducers/helpers/topInformationGenerator'
import confirmTypes from 'helpers/enums/confirmTypes'
import lessonTypes from 'helpers/enums/lessonTypes'
import { localizeWithVars, localize } from 'helpers/translator/localize'
import answerTypes from 'helpers/enums/answerTypes'
import answerStatus from 'helpers/enums/answerStatus'
import iconTypes from 'helpers/enums/iconTypes'
import referenceMediaTypes from 'helpers/enums/referenceMediaTypes'

const defaultTime = 30

const defaultState = {
	skinId: '1',
	activities: [],
	uid: '',
	slideId: '',
	topInformation: [],
	confirms: [],
	modals: [],
	timer: 0,
	isModified: false,
	canReplace: false,
}

const defaultTextMedia = {
	type: answerTypes.TEXT,
	status: '',
	payload: {
		text: '',
	},
}

const defaultImageAsset = {
	id: 0,
	versions: {},
	message: '',
}

const mediaStatus = {
	EMPTY: 'empty',
	PROCESSING: 'processing',
	READY: 'ready',
	UPLOADING: 'uploading',
}

const defaultImageMedia = {
	type: answerTypes.IMAGE,
	status: mediaStatus.EMPTY,
	payload: defaultImageAsset,
}

const mediaTypes = {
	[answerTypes.TEXT]: defaultTextMedia,
	[answerTypes.IMAGE]: defaultImageMedia,
}

const defaultAnswer = {
	correct: false,
	id: '',
	textTouched: true,
	status: mediaStatus.EMPTY,
	media: {},
}

const maxActivities = 50

const defaultIcon = {
	id: 0,
	uploadId: 0,
	type: '',
	versions: {},
	link: '',
	status: '',
	message: '',
}

function addQuestionActivity(activities) {
	const questionId = makeUniqueId()
	return {
		id: questionId,
		state: 'creating',
		collapsed: false,
		answers: [
			{
				...defaultAnswer,
				id: makeUniqueId(),
				media: {
					...defaultTextMedia,
				},
			},
			{
				...defaultAnswer,
				id: makeUniqueId(),
				media: {
					...defaultTextMedia,
				},
			},
		],
		icon: defaultIcon,
		text: '',
		timer: activities.length ? activities[activities.length - 1].timer : defaultTime,
		textTouched: true,
		touched: true,
	}
}

function getAnswerStatus(answer) {
	if ((answer.media.type === answerTypes.TEXT && answer.media.payload.text)
		|| (answer.media.type === answerTypes.IMAGE && answer.media.payload.source)
	) {
		return answerStatus.READY
	}
	return answerStatus.EMPTY
}

function initializeGame(state, action) {
	return {
		...state,
		uid: action.gameUID,
	}
}

function areActivitiesEmpty(activities) {
	if (activities.length > 1 || activities[0].answers.length > 2) {
		return false
	} else {
		activities[0].answers.forEach(answer => {
			if (getAnswerStatus(answer) === answerStatus.READY) {
				return false
			}
		})
		if (activities[0].text) {
			return false
		}
		if (activities[0].icon && activities[0].icon.type) {
			return false
		}
	}
	return true
}

function initializeGameData(state, action) {
	const activities = initializeGameActivitiesData(action)
	return {
		...state,
		...action.payload.game,
		activities,
		canReplace: areActivitiesEmpty(activities) || state.canReplace,
	}
}

function initializeGameActivitiesData(action) {
	return action.payload.game.activities.map(activity => {
		let icon = defaultIcon
		if (activity.asset) {
			icon = {
				id: activity.asset.uid,
				uploadId: 0,
				versions: {
					list: activity.asset.source,
					flash: activity.asset.source,
				},
				status: iconTypes.ICON_STATUS_DONE,
				message: '',
				type: referenceMediaTypes.IMAGE,
			}
		}
		return {
			...activity,
			serverId: activity.uid,
			id: makeUniqueId(),
			state: 'ready',
			textTouched: true,
			touched: true,
			timer: activity.timer,
			icon,
			answers: activity.answers.map(answer => ({
				...answer,
				textTouched: true,
				serverId: answer.uid,
				id: makeUniqueId(),
				status: getAnswerStatus(answer),
			})),
		}
	})
}

function addQuestion(state, action) {
	if (state.activities.length >= maxActivities) {
		action._isCancelled = true
		return {
			...state,
			topInformation: [
				topInformationGenerator({
					message: localizeWithVars('game_activitiesLimit', { maxQuestions: maxActivities }),
					styleType: 'error',
				}),
			],
		}
	} else {
		const question = addQuestionActivity(state.activities)
		action.clientId = question.id
		return {
			...state,
			activities: [...state.activities, question],
			isModified: true,
			canReplace: false,
		}
	}
}

function duplicateQuestion(state, action) {
	if (state.activities.length >= maxActivities) {
		action._isCancelled = true
		return {
			...state,
			topInformation: [
				topInformationGenerator({
					message: localizeWithVars('game_activitiesLimit', { maxQuestions: maxActivities }),
					styleType: 'error',
				}),
			],
		}
	} else {
		const originalActivityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
		const originalActivity = state.activities[originalActivityIndex]
		const newActivity = addQuestionActivity(state.activities)
		newActivity.text = originalActivity.text
		newActivity.icon = originalActivity.icon.status === iconTypes.ICON_STATUS_DONE
			?
			{
				...originalActivity.icon,
			}
			:
			{
				...defaultIcon,
			}
		newActivity.answers = originalActivity.answers.map(answer => ({
			...answer,
			serverId: '',
			id: makeUniqueId(),
		}))
		action.clientId = newActivity.id
		return {
			...state,
			activities: [
				...state.activities.slice(0, originalActivityIndex + 1),
				newActivity,
				...state.activities.slice(originalActivityIndex + 1),
			],
			isModified: true,
			canReplace: false,
		}
	}
}

function confirmActivityDeletion(state, action) {

	const minimumQuestions = 1
	//localizeWithVars('game_activitiesLimit', { maxQuestions: maxActivities }),

	if (state.activities.length <= minimumQuestions) {
		return {
			...state,
			topInformation: [
				topInformationGenerator({
					message: localizeWithVars('creationTool_activity_minimumQuestions', { minimumQuestions }),
					styleType: 'error',
				}),
			],
		}
	} else {
		return {
			...state,
			confirms: [{
				type: confirmTypes.GAME_ACTIVITY_DELETE,
				props: {
					activityId: action.activityId,
				},
			}],
		}
	}
}

function confirmGameCancellation(state) {
	if (!state.isModified) {
		return state
	}

	return {
		...state,
		confirms: [{
			type: confirmTypes.GAME_CANCEL,
			props: {},
		}],

	}
}

function clearConfirms(state) {
	return {
		...state,
		confirms: [],

	}
}

function markActivityToDelete(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	return {
		...state,
		confirms: [],
		activities: [
			...state.activities.slice(0, activityIndex),
			{
				...state.activities[activityIndex],
				state: 'deleting',
			},
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function deleteActivity(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			...state.activities.slice(activityIndex + 1),
		],

	}
}

function connectActivity(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	let newState
	if (activityIndex < 0) {
		newState = state
	} else {
		newState = {
			...state,
			activities: [
				...state.activities.slice(0, activityIndex),
				{
					...state.activities[activityIndex],
					serverId: action.serverId,
					questionUid: action.questionUid,
					state: state.activities[activityIndex].state === 'deleting' ? 'deleting' :'ready',
				},
				...state.activities.slice(activityIndex + 1),
			],

		}
	}
	return newState
}


function addModifiedProperties() {
	return {
		state: 'modified',
		lastModified: makeUniqueId(),
	}
}

function getActivityType(activity) {
	const answers = activity.answers
	return answers.length ? answers[0].media.type : answerTypes.TEXT
}

function getMedia(activity) {
	return mediaTypes[getActivityType(activity)]
}

function createDefaultAnswerFromActivity(activity) {
	return {
		...defaultAnswer,
		id: makeUniqueId(),
		media: {
			...getMedia(activity),
		},
	}
}

function addAnswer(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]
	const maximumAnswers = 4
	if (activity.answers.length >= maximumAnswers) {
		return {
			...state,
			topInformation: [
				topInformationGenerator({
					message: localizeWithVars('creationTool_activity_maximumAnswers', { maximumAnswers }),
					styleType: 'error',
				}),
			],
			isModified: true,
			canReplace: false,
		}
	} else {
		const activityData = {
			...activity,
			...addModifiedProperties(),
			answers: [
				...activity.answers,
				createDefaultAnswerFromActivity(activity),
			],
		}
		return {
			...state,
			activities: [
				...state.activities.slice(0, activityIndex),
				activityData,
				...state.activities.slice(activityIndex + 1),
			],
			isModified: true,
			canReplace: false,
		}
	}
}

function questionDragAnswer(state, action) {
	const newState = {...state}
	newState.activities = [...state.activities]

	// search dragged and replaced question position
	const draggedActivityIndex = state.activities.findIndex(activity => activity.id === action.draggedAnswer.questionId)

	// search dragged and replaced answers positions
	const draggedAnswerIndex = state.activities[draggedActivityIndex].answers.findIndex(
		answer => answer.id === action.draggedAnswer.answerId
	)
	const replacedAnswerIndex = state.activities[draggedActivityIndex].answers.findIndex(
		answer => answer.id === action.replacedAnswer.answerId
	)

	newState.activities[draggedActivityIndex] = {
		...state.activities[draggedActivityIndex],
		...addModifiedProperties(),
	}
	newState.activities[draggedActivityIndex].answers = [...state.activities[draggedActivityIndex].answers]

	// move replaced to dragged position
	newState.activities[draggedActivityIndex].answers[draggedAnswerIndex] = {
		...state.activities[draggedActivityIndex].answers[replacedAnswerIndex],
	}

	// move dragged to replaced position
	newState.activities[draggedActivityIndex].answers[replacedAnswerIndex] = {
		...state.activities[draggedActivityIndex].answers[draggedAnswerIndex],
	}

	return {
		...newState,
		isModified: true,
		canReplace: false,
	}
}

function toggleCollapseActivity(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activityData = {
		...state.activities[activityIndex],
		collapsed: !state.activities[activityIndex].collapsed,
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
	}
}

function collapseAll(state) {
	return {
		...state,
		activities: state.activities.map(activity => ({...activity, collapsed: true})),

	}
}

function expandAll(state) {
	return {
		...state,
		activities: state.activities.map(activity => ({...activity, collapsed: false})),

	}
}

function questionToggleAnswerState(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		touched: true,
		...addModifiedProperties(),
		answers: activity.answers.map(answer => {
			if (action.answerId === answer.id) {
				answer = {
					...answer,
					correct: !answer.correct,
				}
			} else if (answer.correct) {
				answer = {
					...answer,
					correct: false,
				}
			}
			return answer
		}),
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function questionChangeAnswerState(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		...addModifiedProperties(),
		answers: activity.answers.map(answer => {
			if (action.answerId === answer.id) {
				answer = {
					...answer,
					media: {
						type: answerTypes.TEXT,
						payload: {
							text: action.value,
						},
						status: action.value === '' ? mediaStatus.EMPTY : mediaStatus.READY,
					},
					textTouched: true,
					status: action.value === '' ? answerStatus.EMPTY : answerStatus.READY,
				}
			}
			return answer
		}),
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function updateQuestionTimer(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		...addModifiedProperties(),
		timer: action.value,
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function questionChangeState(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		text: action.value,
		textTouched: true,
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function deleteAnswer(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	if (activityIndex !== -1) {
		const activity = state.activities[activityIndex]
		const minimumAnswers = 2
		if (activity.answers.length <= minimumAnswers) {
			return {
				...state,
				topInformation: [
					topInformationGenerator({
						message: localizeWithVars('creationTool_activity_minimumAnswers', { minimumAnswers }),
						styleType: 'error',
					}),

				],
			}
		} else {
			const answerIndex = activity.answers.findIndex(answer => answer.id === action.answerId)
			if (answerIndex !== -1) {
				const activityData = {
					...activity,
					...addModifiedProperties(),
					answers: [
						...activity.answers.slice(0, answerIndex),
						...activity.answers.slice(answerIndex + 1),
					],
				}
				return {
					...state,
					activities: [
						...state.activities.slice(0, activityIndex),
						activityData,
						...state.activities.slice(activityIndex + 1),
					],
					isModified: true,
					canReplace: false,
				}
			}
		}
	}
	return state
}

function setActivityAsSaved(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		state: 'ready',
		answers: activity.answers.map((answer, index) => ({
			...answer,
			serverId: action.answers[index].uid,
		})),
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],

	}
}

function setGameFailedState(state, action) {
	if (action.error.error_code === -10) {
		return {
			...state,
			activities: state.activities.map(activity => ({
				...activity,
				textTouched: false,
				touched: false,
				answers: activity.answers.map(answer => ({
					...answer,
					textTouched: false,
				})),
			})),
			topInformation: [
				topInformationGenerator({
					message: localize('creationTool_activity_missingFields'),
					styleType: 'error',
				}),
			],
		}
	} else {
		return state
	}
}

function closeInformation(state, uid) {
	const index = state.topInformation.findIndex(information => information.uid === uid)
	if (index !== -1) {
		return {
			...state,
			topInformation: [...state.topInformation.slice(0, index), ...state.topInformation.slice(index + 1)],
		}
	} else {
		return state
	}
}

function setQuestionAnswerType(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]

	const filledAnswer = activity.answers.find(answer => answer.status !== answerStatus.EMPTY)

	const newState = {
		...state,
	}

	if (!filledAnswer || action.isConfirmed) {

		const defaultMedia = mediaTypes[action.value]
		const answers = [
			{
				...defaultAnswer,
				id: makeUniqueId(),
				media: {
					...defaultMedia,
				},
			},
			{
				...defaultAnswer,
				id: makeUniqueId(),
				media: {
					...defaultMedia,
				},
			},
		]

		const activityData = {
			...activity,
			...addModifiedProperties(),
			answers,
		}

		newState.activities = [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		]
		newState.confirms = []

	} else {

		action._isCancelled = true

		newState.confirms = [{
			type: confirmTypes.GAME_ACTIVITY_ANSWER_TYPE_TOGGLE,
			props: {
				activityId: action.activityId,
				value: action.value,
			},
		}]
	}

	return newState

}

function previewMedia(state, action) {
	const activity = state.activities.find(activity => activity.id === action.questionId)
	const answer = activity.answers.find(answer => answer.id === action.answerId)
	return {
		...state,
		modals: [
			{
				type: 'media_preview',
				modalName: 'media_preview',
				params: {
					mediaType: 'image',
					mediaVersions: {
						flash: answer.media.payload.source,
					},
				},
			},
		],
	}
}

function closeModal(state, action) {
	const modals = state.modals
	const modalIndex = modals.findIndex(modal => modal.modalName === action.modalName)
	if (modalIndex === -1) {
		return state
	} else {
		return {
			...state,
			modals: [...modals.slice(0, modalIndex), ...modals.slice(modalIndex + 1)],
		}
	}
}

function setAnswerMediaSavingState(state, action) {

	const activityIndex = state.activities.findIndex(activity => activity.id === action.questionId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		answers: activity.answers.map(answer => {
			if (action.answerId === answer.id) {
				answer = {
					...answer,
					media: {
						...answer.media,
						status: mediaStatus.UPLOADING,
					},
					textTouched: true,
					status: answerStatus.UPLOADING,
				}
			}
			return answer
		}),
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function setAnswerMediaSavedState(state, action) {

	const activityIndex = state.activities.findIndex(activity => activity.id === action.questionId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		answers: activity.answers.map(answer => {
			if (action.answerId === answer.id) {
				answer = {
					...answer,
					...action.answer,
					media: {
						...action.answer.media,
						status: mediaStatus.READY,
					},
					textTouched: true,
					status: answerStatus.READY,
				}
			}
			return answer
		}),
	}
	return {
		...state,
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function restoreAnswerMediaState(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.questionId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		...addModifiedProperties(),
		answers: activity.answers.map(answer => {
			if (action.answerId === answer.id) {
				return {
					...answer,
					...createDefaultAnswerFromActivity(activity),
				}
			}
			return answer
		}),
	}
	return {
		...state,
		topInformation: [
			topInformationGenerator({
				message: localize('upload_image_error'),
				styleType: 'error',
			}),
		],
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function deleteAnswerMedia(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.activityId)
	const activity = state.activities[activityIndex]
	const activityData = {
		...activity,
		...addModifiedProperties(),
		answers: activity.answers.map(answer => {
			if (action.answerId === answer.id) {
				return createDefaultAnswerFromActivity(activity)
			}
			return answer
		}),
	}
	return {
		...state,
		topInformation: [],
		activities: [
			...state.activities.slice(0, activityIndex),
			activityData,
			...state.activities.slice(activityIndex + 1),
		],
		isModified: true,
		canReplace: false,
	}
}

function moveQuestion(state, action) {
	const newState = {...state}
	newState.isModified = true
	newState.canReplace = false
	newState.activities = [...state.activities]

	const draggedIndex = state.activities.findIndex(question => question.id === action.draggedQuestion)
	const replacedIndex = state.activities.findIndex(question => question.id === action.replacedQuestion)

	newState.activities[draggedIndex] = state.activities[replacedIndex]
	newState.activities[replacedIndex] = state.activities[draggedIndex]
	return newState
}

function mergeActivityServerData(state, action) {
	const activityIndex = state.activities.findIndex(activity => activity.id === action.clientActivityData.id)
	const currentActivityData = state.activities[activityIndex]
	if (activityIndex !== -1) {
		const activity = {
			...currentActivityData,
			uid: action.serverActivityData.uid,
			serverId: action.serverActivityData.uid,
			state: currentActivityData.state === 'deleting' ? 'deleting' :'ready',
			questionUid: action.serverActivityData.questionUid,
			answers: currentActivityData.answers.map(answer => {
				const cachedAnswerIndex = action.clientActivityData.answers.findIndex(
					cachedAnswer => cachedAnswer.id === answer.id
				)
				if (cachedAnswerIndex !== -1) {
					return {
						...answer,
						serverId: action.serverActivityData.answers[cachedAnswerIndex].uid,
						uid: action.serverActivityData.answers[cachedAnswerIndex].uid,
					}
				} else {
					return answer
				}
			}),
		}
		return {
			...state,
			activities: [
				...state.activities.slice(0, activityIndex),
				activity,
				...state.activities.slice(activityIndex + 1),
			],
		}
	}
	return state
}

function showQuestionMediaLoading(state, action) {
	return {
		...state,
		activities: state.activities.map(activity => {
			if (activity.id === action.questionId) {
				return {
					...activity,
					icon: {
						...activity.icon,
						status: 'uploading',
						message: '',
					},
				}
			} else {
				return activity
			}
		}),
	}
}

function uploadQuestionMediaSuccess(state, action) {
	return {
		...state,
		isModified: true,
		canReplace: false,
		activities: state.activities.map(activity => {
			if (activity.id === action.questionId) {
				return {
					...activity,
					icon: {
						...activity.icon,
						id: action.icon.uid,
						versions: {
							list: action.icon.source,
							flash: action.icon.source,
						},
						status: iconTypes.ICON_STATUS_DONE,
						message: '',
						type: referenceMediaTypes.IMAGE,
					},
				}
			} else {
				return activity
			}
		}),
	}
}

function deleteQuestionReferenceMedia(state, action) {
	return {
		...state,
		isModified: true,
		canReplace: false,
		activities: state.activities.map(activity => {
			if (activity.id === action.questionId) {
				return {
					...activity,
					icon: {
						...defaultIcon,
					},
				}
			} else {
				return activity
			}
		}),
	}
}

function uploadQuestionMediaFailed(state, action) {
	return {
		...state,
		activities: state.activities.map(activity => {
			if (activity.id === action.questionId) {
				return {
					...activity,
					icon: {
						...activity.icon,
						message: action.message,
						status: '',
					},
				}
			} else {
				return activity
			}
		}),
	}
}

function handleActivityBanksSelected(state, action) {
	if (!state.canReplace && action.lesson.data.type === lessonTypes.TIME_TO_CLIMB) {
		return {
			...state,
			confirms: [{
				type: confirmTypes.ACTIVITY_REPLACE,
				props: {
					lesson: action.lesson,
				},
			}],
		}
	} else {
		return state
	}
}

function handleActivityBanksSelectedConfirmed(state) {
	const newState = {
		...state,
		canReplace: true,
	}
	return clearConfirms(newState)
}

function storeSlideId(state, action) {
	return {
		...state,
		slideId: action.payload.id,
	}
}

const gameReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.GAME_INITIALIZE_CREATION_TOOL:
		case actionTypes.GAME_UPDATE_CREATION_TOOL:
		case actionTypes.GAME_INITIALIZE_STANDALONE:
			return initializeGame(state, action)
		case actionTypes.GAME_INITIALIZE_DATA_SUCCESS:
			return initializeGameData(state, action)
		case actionTypes.GAME_ADD_QUESTION:
			return addQuestion(state, action)
		case actionTypes.GAME_DELETE_ACTIVITY:
			return confirmActivityDeletion(state, action)
		case actionTypes.GAME_DELETE_ACTIVITY_CONFIRMED:
			return markActivityToDelete(state, action)
		case actionTypes.GAME_DELETE_ACTIVITY_CANCEL:
		case actionTypes.GAME_CANCEL_CANCELLED:
		case actionTypes.GAME_CANCEL_CONFIRMED:
		case actionTypes.GAME_QUESTION_SELECT_ANSWER_TYPE_CANCELLED:
		case actionTypes.ACTIVITY_BANKS_ACTIVITY_SELECTED_CANCELLED:
			return clearConfirms(state, action)
		case actionTypes.ACTIVITY_BANKS_ACTIVITY_SELECTED_CONFIRMED:
			return handleActivityBanksSelectedConfirmed(state, action)
		case actionTypes.GAME_DELETE_ACTIVITY_SUCCESS:
			return deleteActivity(state, action)
		case actionTypes.GAME_ACTIVITY_CREATED_SUCCESS:
			return connectActivity(state, action)
		case actionTypes.GAME_QUESTION_ADD_ANSWER:
			return addAnswer(state, action)
		case actionTypes.GAME_QUESTION_ANSWER_DRAG:
			return questionDragAnswer(state, action)
		case actionTypes.GAME_COLLAPSE_ACTIVITY:
			return toggleCollapseActivity(state, action)
		case actionTypes.GAME_COLLAPSE_ALL:
			return collapseAll(state, action)
		case actionTypes.GAME_EXPAND_ALL:
			return expandAll(state, action)
		case actionTypes.GAME_QUESTION_ANSWER_CORRECT_TOGGLE:
			return questionToggleAnswerState(state, action)
		case actionTypes.GAME_QUESTION_ANSWER_CHANGE:
			return questionChangeAnswerState(state, action)
		case actionTypes.GAME_QUESTION_CHANGE:
			return questionChangeState(state, action)
		case actionTypes.GAME_QUESTION_ANSWER_DELETE:
			return deleteAnswer(state, action)
		case actionTypes.GAME_QUESTION_TIMER_SAVE:
			return updateQuestionTimer(state, action)
		case actionTypes.GAME_ACTIVITY_SAVED:
			return setActivityAsSaved(state, action)
		case actionTypes.GAME_SAVE_FAILED:
			return setGameFailedState(state, action)
		case actionTypes.CLOSE_TOP_INFORMATION:
			return closeInformation(state, action.uid)
		case actionTypes.GAME_CANCEL:
			return confirmGameCancellation(state, action)
		case actionTypes.GAME_QUESTION_SELECT_ANSWER_TYPE:
			return setQuestionAnswerType(state, action)
		case actionTypes.GAME_UPLOAD_ANSWER_MEDIA_PREVIEW:
			return previewMedia(state, action)
		case actionTypes.CLOSE_MODAL:
			return closeModal(state, action)
		case actionTypes.GAME_UPLOAD_ANSWER_MEDIA_SELECTED:
			return setAnswerMediaSavingState(state, action)
		case actionTypes.GAME_UPLOAD_ANSWER_MEDIA_FAILED:
			return restoreAnswerMediaState(state, action)
		case actionTypes.GAME_UPLOAD_ANSWER_MEDIA_SUCCESS:
			return setAnswerMediaSavedState(state, action)
		case actionTypes.GAME_UPLOAD_ANSWER_MEDIA_DELETE:
			return deleteAnswerMedia(state, action)
		case actionTypes.GAME_DRAG_QUESTION:
			return moveQuestion(state, action)
		case actionTypes.GAME_QUESTION_DUPLICATE:
			return duplicateQuestion(state, action)
		case actionTypes.GAME_QUESTION_DUPLICATE_SUCCESS:
			return mergeActivityServerData(state, action)
		case actionTypes.GAME_UPLOAD_QUESTION_MEDIA_SELECTED:
			return showQuestionMediaLoading(state, action)
		case actionTypes.GAME_UPLOAD_QUESTION_MEDIA_SUCCESS:
			return uploadQuestionMediaSuccess(state, action)
		case actionTypes.GAME_UPLOAD_QUESTION_MEDIA_ERROR:
		case actionTypes.GAME_UPLOAD_QUESTION_MEDIA_FAILED:
			return uploadQuestionMediaFailed(state, action)
		case actionTypes.GAME_UPLOAD_QUESTION_MEDIA_DELETE:
			return deleteQuestionReferenceMedia(state, action)
		case actionTypes.ACTIVITY_BANKS_ACTIVITY_SELECTED:
			return handleActivityBanksSelected(state, action)
		case actionTypes.GAME_INITIALIZE_SLIDE_DATA_SUCCESS:
			return storeSlideId(state, action)
		default:
			return state
	}
}

export default gameReducer

export {
	addQuestionActivity,
	addModifiedProperties,
	defaultAnswer,
	defaultState,
	defaultTextMedia,
	defaultImageMedia,
	mediaStatus,
}
